import React, { useEffect, useState } from "react";
import {
    Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
    InputAdornment, CircularProgress, Grid, MenuItem
} from "@mui/material";
import { Add, Edit, Delete, Search, Book, BarcodeReader } from "@mui/icons-material";
import {
    collection, query, where, getDocs, addDoc, updateDoc,
    deleteDoc, doc, getDoc, orderBy
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";

function Books() {
    const [books, setBooks] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [schoolId, setSchoolId] = useState(null);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialog State
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        bookName: "", author1: "", author2: "", author3: "", author4: "",
        barcodeNo: "", classId: "", language: "", publication: "", rate: ""
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const getInitialData = async () => {
            try {
                const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                const sId = userSnap.data()?.schoolId;
                setSchoolId(sId);

                // Fetch Courses (Classes)
                const coursesSnap = await getDocs(query(collection(db, "courses"), where("schoolId", "==", sId)));
                const coursesList = coursesSnap.docs.map(d => ({
                    id: d.id,
                    name: d.data().name // Ensure this matches your Firestore field (name vs className)
                }));
                setClasses(coursesList);

                if (sId) fetchBooks(sId);
            } catch (e) { console.error("Initial Data Error:", e); }
        };
        getInitialData();
    }, []);


    const fetchBooks = async (sId) => {
        setLoading(true);
        try {
            const q = query(collection(db, "books"), where("schoolId", "==", sId));
            const snap = await getDocs(q);
            setBooks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const handleSubmit = async () => {
        if (!formData.bookName || !formData.author1) {
            return Swal.fire("Error", "Book Name and Author 1 are mandatory", "error");
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                schoolId,
                rate: formData.rate.toString(), // Stored as string per your requirements
            };

            if (editId) {
                await updateDoc(doc(db, "books", editId), payload);
            } else {
                await addDoc(collection(db, "books"), payload);
            }

            Swal.fire("Success", "Book saved successfully", "success");
            setOpen(false);
            resetForm();
            fetchBooks(schoolId);
        } catch (e) {
            Swal.fire("Error", e.message, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        const res = await Swal.fire({
            title: 'Delete Book?',
            text: "This record will be permanently removed.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        });

        if (res.isConfirmed) {
            await deleteDoc(doc(db, "books", id));
            fetchBooks(schoolId);
        }
    };

    const resetForm = () => {
        setFormData({
            bookName: "", author1: "", author2: "", author3: "", author4: "",
            barcodeNo: "", classId: "", language: "", publication: "", rate: ""
        });
        setEditId(null);
    };

    const filteredBooks = books.filter(b =>
        b.bookName.toLowerCase().includes(search.toLowerCase()) ||
        b.author1.toLowerCase().includes(search.toLowerCase()) ||
        b.barcodeNo.includes(search)
    );

    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight={700}>Library Books</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => { resetForm(); setOpen(true); }}>
                    Add New Book
                </Button>
            </Stack>

            <Card sx={{ p: 2, mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search by Book Name, Author, or Barcode..."
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                />
            </Card>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell><b>Sr No</b></TableCell>
                            <TableCell><b>Book Name</b></TableCell>
                            <TableCell><b>Primary Author</b></TableCell>
                            <TableCell><b>Publication</b></TableCell>
                            <TableCell><b>Class</b></TableCell>
                            <TableCell><b>Rate</b></TableCell>
                            <TableCell align="center"><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={30} sx={{ m: 3 }} /></TableCell></TableRow>
                        ) : filteredBooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((book, index) => (
                            <TableRow key={book.id} hover>
                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{book.bookName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{book.barcodeNo}</Typography>
                                </TableCell>
                                <TableCell>{book.author1}</TableCell>
                                <TableCell>{book.publication}</TableCell>
                                <TableCell>
                                    {/* We look inside the 'classes' (courses) state to find the ID 
      that matches the book's classId 
  */}
                                    {classes.find(c => c.id === book.classId)?.name || (
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {book.classId?.substring(0, 5)}...
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>₹{book.rate}</TableCell>
                                <TableCell align="center">
                                    <IconButton onClick={() => { setEditId(book.id); setFormData(book); setOpen(true); }} color="primary"><Edit fontSize="small" /></IconButton>
                                    <IconButton onClick={() => handleDelete(book.id)} color="error"><Delete fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredBooks.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>

            {/* Add/Edit Modal */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editId ? "Update Book Details" : "Add New Book to Library"}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Book Name" fullWidth value={formData.bookName} onChange={e => setFormData({ ...formData, bookName: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Barcode No" fullWidth value={formData.barcodeNo} onChange={e => setFormData({ ...formData, barcodeNo: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Author 1 (Primary)" fullWidth value={formData.author1} onChange={e => setFormData({ ...formData, author1: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Author 2" fullWidth value={formData.author2} onChange={e => setFormData({ ...formData, author2: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Author 3" fullWidth value={formData.author3} onChange={e => setFormData({ ...formData, author3: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Author 4" fullWidth value={formData.author4} onChange={e => setFormData({ ...formData, author4: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField select label="Class" fullWidth value={formData.classId} onChange={e => setFormData({ ...formData, classId: e.target.value })}>
                                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Language" fullWidth value={formData.language} onChange={e => setFormData({ ...formData, language: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Rate" type="number" fullWidth value={formData.rate} onChange={e => setFormData({ ...formData, rate: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Publication" fullWidth value={formData.publication} onChange={e => setFormData({ ...formData, publication: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <CircularProgress size={24} /> : "Save Book"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Books;